import React from 'react';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';
import moment from 'moment';
import CurrencyUsdIcon from 'mdi-react/CurrencyUsdIcon';
import renderSelectField from '../../../../shared/components/form/Select';
import Tooltip from '../../../../shared/components/widgets/Tooltip';
import { renderFieldForm, RenderDatePicker } from '../../shared/formComponents';
import {
  BUDGET_DRIVER_OPTIONS,
  STATE_OPTIONS,
  AD_TYPE_GROUPS,
} from '../../shared/constants';

/**
 * Part 2: Fields that appear AFTER type-specific fields
 * Includes: Dates, Budget Driver, Budget Allocation (item only), Amounts, State
 */
const CommonBudgetFields = ({
  setupFlow,
  agencyInternal,
  budgetDriver,
  budgetType,
  budgetAllocationSpSd,
  budgetAllocationSb,
  budgetAllocationSt,
  monthOptions,
  selectedSeller,
  dispatch,
  change,
  cleanNumber,
  isExternalBudgetGroup,
}) => (
  <>
    {/* 21. Start Date */}
    <div className="form__form-group">
      <div style={{ width: '50%', height: '60px', float: 'left' }}>
        <span className="form__form-group-label">Start Date</span>
        <div>
          <Field
            name="startDate"
            type="text"
            handleDateChange={(newDate) => dispatch(change('budgetGroupEditModal', 'startDate', newDate ? moment(newDate) : null))}
            component={RenderDatePicker}
            minDate={isExternalBudgetGroup ? undefined : moment().startOf('day')}
            required
          />
        </div>
      </div>
      {/* 22. End Date */}
      <div style={{ marginLeft: '50%', height: '60px' }}>
        <span className="form__form-group-label">End Date</span>
        <div className="form__form-group-field">
          <Field
            name="endDate"
            type="text"
            handleDateChange={(newDate) => dispatch(change('budgetGroupEditModal', 'endDate', newDate ? moment(newDate) : null))}
            component={RenderDatePicker}
          />
        </div>
      </div>
    </div>

    {/* 23. Budget Driver */}
    <div className="form__form-group">
      <span className="form__form-group-label">Budget Driver</span>
      <div className="form__form-group-field">
        <Field
          name="budgetDriver"
          type="text"
          component={renderSelectField}
          options={BUDGET_DRIVER_OPTIONS}
        />
      </div>
    </div>

    {/* 24. Budget Allocation by Ad Type (item type only, not brand or DSP or External) */}
    {budgetType?.value !== 'brand' && budgetType?.value !== 'dsp' && !isExternalBudgetGroup && (
      <div className="form__form-group">
        <span className="form__form-group-label typography-message">
          Budget Allocation by Ad Type <Tooltip id="budgetAllocation" msg="Allocate budget percentages across ad types. Total must equal 100%." placement="top" />
        </span>
        <div className="form__form-group-field">
          <div style={{ display: 'block', marginBottom: '10px', padding: '10px', borderRadius: '4px', border: '1px solid rgba(128, 128, 128, 0.2)' }}>
            {AD_TYPE_GROUPS.map((adType) => {
              let fieldName = 'budgetAllocationSt';
              if (adType.key === 'sp_sd') {
                fieldName = 'budgetAllocationSpSd';
              } else if (adType.key === 'sb') {
                fieldName = 'budgetAllocationSb';
              }
              return (
                <div key={adType.key} style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', clear: 'both' }}>
                  <span style={{ width: '120px', fontWeight: '500' }}>{adType.label}:</span>
                  <div style={{ width: '80px', marginRight: '10px' }}>
                    <Field
                      name={fieldName}
                      type="text"
                      component={renderFieldForm}
                      placeholder="0"
                      format={cleanNumber}
                    />
                  </div>
                  <span>%</span>
                </div>
              );
            })}
            {(budgetAllocationSpSd || budgetAllocationSb || budgetAllocationSt) && (
              <div style={{ marginTop: '10px', padding: '8px', borderRadius: '4px', border: '1px solid rgba(70, 187, 253, 0.3)', backgroundColor: 'rgba(70, 187, 253, 0.1)' }}>
                <strong>Total: {((parseFloat(budgetAllocationSpSd) || 0) + (parseFloat(budgetAllocationSb) || 0) + (parseFloat(budgetAllocationSt) || 0)).toFixed(2)}%</strong>
                {((parseFloat(budgetAllocationSpSd) || 0) + (parseFloat(budgetAllocationSb) || 0) + (parseFloat(budgetAllocationSt) || 0)) !== 100 && (
                  <span style={{ color: '#ff4861', marginLeft: '10px' }}>⚠ Must equal 100%</span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    )}

    {/* 25. Daily Budget Amount */}
    {budgetDriver?.value === 'daily' && (
      <div className="form__form-group">
        <span className="form__form-group-label">Total Daily Budget Amount in {selectedSeller.currencyCode} for all Items</span>
        <div className="form__form-group-field">
          <div className="form__form-group-icon">
            <CurrencyUsdIcon />
          </div>
          <Field
            name="baseDailyBudgetAmount"
            type="text"
            component={renderFieldForm}
            format={cleanNumber}
          />
        </div>
      </div>
    )}

    {/* 25. Monthly Budget Amount */}
    {budgetDriver?.value === 'monthly' && (
      <div className="form__form-group">
        <span className="form__form-group-label">Monthly Budget Amount in {selectedSeller.currencyCode}</span>
        {monthOptions && monthOptions.map((month) => (
          <div className="form__form-group-field" style={{ padding: '2px' }} key={month.name}>
            <span style={{ paddingRight: '10px', paddingTop: '5px', width: '25%', textAlign: 'right' }}>
              {month.label}:
            </span>
            <div className="form__form-group-icon">
              <CurrencyUsdIcon />
            </div>
            <Field
              name={month.name}
              type="text"
              component={renderFieldForm}
              format={cleanNumber}
            />
          </div>
        ))}
      </div>
    )}

    {/* 26. Reallocation (daily + advanced + admin) */}
    {budgetDriver?.value === 'daily' && setupFlow?.value === 'advanced' && agencyInternal?.value && (
      <div className="form__form-group">
        <span className="form__form-group-label">Total Daily Reallocation Budget Amount in {selectedSeller.currencyCode}</span>
        <div className="form__form-group-field">
          <div className="form__form-group-icon">
            <CurrencyUsdIcon />
          </div>
          <Field
            name="reallocation"
            type="text"
            component={renderFieldForm}
            format={cleanNumber}
          />
        </div>
      </div>
    )}

    {/* 27. Budget Group State */}
    <div className="form__form-group">
      <span className="form__form-group-label">Budget Group State</span>
      <div className="form__form-group-field">
        <Field
          name="state"
          type="text"
          component={renderSelectField}
          options={STATE_OPTIONS}
        />
      </div>
    </div>
  </>
);

CommonBudgetFields.propTypes = {
  setupFlow: PropTypes.shape(),
  agencyInternal: PropTypes.shape(),
  budgetDriver: PropTypes.shape(),
  budgetType: PropTypes.shape(),
  budgetAllocationSpSd: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  budgetAllocationSb: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  budgetAllocationSt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  monthOptions: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  selectedSeller: PropTypes.shape().isRequired,
  dispatch: PropTypes.func.isRequired,
  change: PropTypes.func.isRequired,
  cleanNumber: PropTypes.func.isRequired,
  isExternalBudgetGroup: PropTypes.bool,
};

CommonBudgetFields.defaultProps = {
  setupFlow: null,
  agencyInternal: null,
  budgetDriver: null,
  budgetType: null,
  budgetAllocationSpSd: '',
  budgetAllocationSb: '',
  budgetAllocationSt: '',
  isExternalBudgetGroup: false,
};

export default CommonBudgetFields;
